import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import ProductDetailModal from "./ProductDetailModal";

// --- Skeleton Loader Component ---
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm animate-pulse flex flex-col overflow-hidden h-[380px]">
    <div className="w-full h-[260px] bg-slate-200" />
    <div className="p-4 space-y-2">
      <div className="h-5 bg-slate-200 rounded w-3/4" />
      <div className="h-4 bg-slate-200 rounded w-1/2" />
    </div>
  </div>
);

export const ProductGridmainSection = () => {
  const { id, main_cat_id } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  // Fetch Products
  const fetchProducts = useCallback(
    async (page = 1) => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(
          `https://webadmin.naturx.co.in/api/getproduct?main_cat_id=${main_cat_id}&cat_id=${
            id || 0
          }&page=${page}`,
        );
        if (!response.ok) throw new Error("Failed to load products");
        const data = await response.json();
        setProducts(data.data?.data || []);
        setTotalPages(data.data?.last_page || 1);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [id, main_cat_id],
  );

  // Fetch Categories to get banner data
  useEffect(() => {
    if (!main_cat_id) return;

    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `https://webadmin.naturx.co.in/api/get_category?main_cat_id=${main_cat_id}`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch collection data.");
        }
        const data = await response.json();
        setCategories(data.data || []);
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchCategories();
  }, [main_cat_id]);

  useEffect(() => {
    fetchProducts(currentPage);
  }, [fetchProducts, currentPage]);

  // Find the current active category based on URL `id`
  const selectedCategoryData = useMemo(() => {
    if (!id || id === "0") {
      return {
        name: "All Products",
        description: "Explore our comprehensive catalog selection.",
        image:
          "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80&w=1200", // Fallback image
      };
    }
    return categories.find((cat) => String(cat.id) === String(id)) || null;
  }, [categories, id]);

  const handleImageLoad = (productId) => {
    setLoadedImages((prev) => ({ ...prev, [productId]: true }));
  };

  const memoizedProducts = useMemo(() => products, [products]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col justify-center items-center text-center px-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl max-w-md">
          <p className="text-red-600 font-medium mb-2">Something went wrong</p>
          <p className="text-sm text-red-500">{error}</p>
          <button
            onClick={() => fetchProducts(currentPage)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-[75vh] pb-16 bg-slate-50/50">
      {/* HERO BANNER - FULL WIDTH */}
      <section className="w-full mb-12">
        <div className="relative w-full overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-800">
          {/* GRADIENT BLOBS */}
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-orange-500/30 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-pink-500/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 flex flex-col lg:flex-row w-full min-h-[380px]">
            {/* IMAGE CONTAINER */}
            {selectedCategoryData?.image && (
              <div className="w-full h-[320px] sm:h-[380px] lg:h-auto lg:w-2/5 xl:w-1/3 flex-shrink-0">
                <img
                  src={selectedCategoryData.image}
                  alt={selectedCategoryData?.name || "Category Banner"}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* TEXT CONTENT */}
            <div className="flex-1 text-white p-6 sm:p-8 lg:p-10 flex flex-col justify-center max-w-5xl">
              <p className="text-orange-400 uppercase text-xs sm:text-sm font-semibold mb-3">
                Explore Product
              </p>

              <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-4">
                {selectedCategoryData?.name || "Loading Collection..."}
              </h2>

              <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-3xl">
                {selectedCategoryData?.description || ""}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* --- Header Banner Section --- */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200/80 pb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-600 bg-orange-50 px-3 py-1 rounded-full border border-orange-200/60 inline-block mb-3">
              Exclusive Collection
            </span>
            <h1 className="text-xl lg:text-2xl font-semibold text-slate-900 tracking-tight">
              Professional Products
            </h1>
          </div>
          {/* <p className="text-sm text-slate-500 max-w-xs">
            Discover precision-crafted catalog patterns curated for performance
            and reliability.
          </p> */}
        </div>

        {/* --- Product Grid --- */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : memoizedProducts.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl border border-slate-200/60 shadow-sm">
            <h3 className="text-lg font-bold text-slate-800">
              No Products Found
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              Check back later for updates to this collection.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {memoizedProducts.map((product) => {
              const isImgLoaded = loadedImages[product.id];

              return (
                <article
                  key={product.id}
                  className="group relative bg-white rounded-2xl border border-slate-100/80 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 ease-out overflow-hidden flex flex-col justify-between"
                >
                  {/* Full-Width Image Container */}
                  <div className="relative w-full h-[260px] overflow-hidden bg-slate-100">
                    {!isImgLoaded && (
                      <div className="absolute inset-0 bg-slate-200 animate-pulse" />
                    )}
                    <a
                      href={`/product/${product.id}/${currentPage}/${main_cat_id}/${
                        id || 0
                      }/${encodeURIComponent(product.title || "")}`}
                      className="w-full h-full block relative z-10"
                      aria-label={`View details for ${product.title}`}
                    >
                      <img
                        src={product.image}
                        alt={
                          product.title
                            ? `Image of ${product.title}`
                            : "Product thumbnail"
                        }
                        loading="lazy"
                        onLoad={() => handleImageLoad(product.id)}
                        className={`w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105 ${
                          isImgLoaded ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </a>

                    {/* --- Mobile View Icon --- */}
                    <button
                      onClick={() => setSelectedProduct(product)}
                      className="md:hidden absolute top-3 right-3 z-20 bg-white/95 text-slate-800 active:scale-95 transition-transform p-2.5 rounded-full shadow-md border border-slate-200/80 flex items-center justify-center"
                      aria-label={`Quick view ${product.title}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-orange-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>

                    {/* --- Desktop Hover View Icon --- */}
                    <button
                      onClick={() => setSelectedProduct(product)}
                      className="hidden md:flex absolute bottom-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-md text-slate-800 hover:bg-orange-600 hover:text-white p-2.5 rounded-full shadow-lg border border-white/40 items-center justify-center"
                      aria-label={`Quick view ${product.title}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 flex flex-col justify-between flex-grow">
                    <div>
                      <span className="text-[11px] font-semibold text-orange-600 uppercase tracking-wider block mb-1">
                        {product.category || "General"}
                      </span>
                      <h3 className="text-base font-bold text-slate-800 group-hover:text-orange-600 transition-colors line-clamp-1">
                        {product.title}
                      </h3>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* --- Modern Pagination Bar --- */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-16 gap-3">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-sm shadow-sm hover:border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white transition flex items-center gap-2"
              aria-label="Previous Page"
            >
              Prev
            </button>

            <span className="px-4 py-2.5 bg-white border border-slate-200/80 rounded-xl text-sm font-semibold text-slate-700 shadow-sm">
              Page{" "}
              <span className="text-orange-600 font-bold">{currentPage}</span>{" "}
              of {totalPages}
            </span>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold text-sm shadow-sm hover:border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white transition flex items-center gap-2"
              aria-label="Next Page"
            >
              Next
            </button>
          </div>
        )}

        {/* --- Product Detail Modal --- */}
        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
          />
        )}
      </div>
    </section>
  );
};
